/* ================ ONLINE PAYMENT - CONSTANTS ================ */

import { UserAccountType, SourceStatusEnum } from '@am3nd-org/pointcraft-types';

import { paths } from 'src/routes/paths';

import { EmployeeRole } from '../schemas/employee';
import type {
  TimeFilter,
  PaymentMethod,
  TransactionType,
  PermissionOption,
  TransactionStatus,
} from '../types';

/* =================== THEME COLORS =================== */

export const COLORS = {
  primary: '#1e293b',
  primaryDark: '#0f172a',
  primaryHover: '#334155',
  secondary: '#64748b',
  background: '#f8fafc',
  cardBg: '#ffffff',
  border: '#e2e8f0',

  // METRICS COLORS
  success: '#16a34a',
  successBg: '#dcfce7',
  info: '#2563eb',
  infoBg: '#dbeafe',
  warning: '#f59e0b',
  warningBg: '#fef3c7',
  error: '#ef4444',
  errorBg: '#fee2e2',
  purple: '#6366f1',
  purpleBg: '#e0e7ff',
  pink: '#ec4899',
  pinkBg: '#fce7f3',
  neutral: '#64748b',
  neutralBg: '#f1f5f9',

  // TEXT COLORS
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
} as const;

/* =================== TIME FILTERS =================== */

export const TIME_FILTERS: { value: TimeFilter; label: string }[] = [
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
];

/* =================== TRANSACTION STATUSES =================== */

export const TRANSACTION_STATUSES: {
  value: TransactionStatus;
  label: string;
  color: string;
  bgColor: string;
}[] = [
  // Success states
  { value: SourceStatusEnum.PAID, label: 'Paid', color: COLORS.success, bgColor: COLORS.successBg },
  {
    value: SourceStatusEnum.OVERPAID,
    label: 'Overpaid',
    color: COLORS.success,
    bgColor: COLORS.successBg,
  },
  // Pending states
  {
    value: SourceStatusEnum.UNPAID,
    label: 'Unpaid',
    color: COLORS.warning,
    bgColor: COLORS.warningBg,
  },
  {
    value: SourceStatusEnum.PARTIAL,
    label: 'Partial',
    color: COLORS.warning,
    bgColor: COLORS.warningBg,
  },
  // Warning state
  {
    value: SourceStatusEnum.OVERDUE,
    label: 'Overdue',
    color: COLORS.error,
    bgColor: COLORS.errorBg,
  },
  // Refund states
  {
    value: SourceStatusEnum.REFUNDED,
    label: 'Refunded',
    color: COLORS.purple,
    bgColor: COLORS.purpleBg,
  },
  {
    value: SourceStatusEnum.PARTIALLY_REFUNDED,
    label: 'Partial Refund',
    color: COLORS.purple,
    bgColor: COLORS.purpleBg,
  },
  // Cancelled state (neutral)
  {
    value: SourceStatusEnum.CANCELLED,
    label: 'Cancelled',
    color: COLORS.neutral,
    bgColor: COLORS.neutralBg,
  },
  // Failed states
  {
    value: SourceStatusEnum.FAILED_FINAL,
    label: 'Failed',
    color: COLORS.error,
    bgColor: COLORS.errorBg,
  },
];

/* =================== TRANSACTION TYPES =================== */

export const TRANSACTION_TYPES: {
  value: TransactionType;
  label: string;
}[] = [
  { value: 'payment', label: 'Payment' },
  { value: 'refund', label: 'Refund' },
  { value: 'payout', label: 'Payout' },
];

/* =================== PAYMENT METHODS =================== */

export const PAYMENT_METHODS: {
  value: PaymentMethod;
  label: string;
  icon: string;
}[] = [
  { value: 'cash', label: 'Cash', icon: 'solar:cash-out-bold' },
  { value: 'card', label: 'Card', icon: 'solar:card-bold' },
  { value: 'mobile', label: 'Mobile', icon: 'solar:smartphone-bold' },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: 'solar:buildings-3-bold' },
];

/* =================== PAYMENT TYPE OPTIONS (FOR ADD TRANSACTION) =================== */

export type PaymentTypeValue = 'ach' | 'card';

export const PAYMENT_TYPE_OPTIONS: {
  value: PaymentTypeValue;
  label: string;
  disabled: boolean;
  icon: string;
}[] = [
  { value: 'ach', label: 'ACH', disabled: true, icon: 'solar:buildings-3-bold' },
  { value: 'card', label: 'Card Payment', disabled: false, icon: 'solar:card-bold' },
];

/* =================== NAVIGATION ITEMS =================== */

export const NAV_ITEMS = [
  {
    title: 'Dashboard',
    path: paths.pointPay.dashboard,
    icon: 'solar:widget-5-bold',
  },
  {
    title: 'Transactions',
    path: paths.pointPay.transactions,
    icon: 'solar:bill-list-bold',
  },
  {
    title: 'Payment Links',
    path: paths.pointPay.paymentLinks,
    icon: 'solar:link-circle-bold',
  },
  {
    title: 'Customers',
    path: paths.pointPay.customers,
    icon: 'solar:users-group-rounded-bold',
  },
  {
    title: 'Settings',
    path: paths.pointPay.settings,
    icon: 'solar:settings-bold',
  },
] as const;

/* =================== METRIC CARDS CONFIGURATION =================== */

export const METRIC_CARDS_CONFIG = [
  {
    id: 'todayRevenue',
    title: "Today's Revenue",
    icon: 'solar:sun-bold',
    color: COLORS.success,
    bgColor: COLORS.successBg,
    format: 'currency',
  },
  {
    id: 'totalRevenue',
    title: 'Total Revenue This period',
    icon: 'solar:dollar-bold',
    color: COLORS.success,
    bgColor: COLORS.successBg,
    format: 'currency',
  },
  {
    id: 'avgTransaction',
    title: 'Avg Transaction',
    icon: 'solar:chart-2-bold',
    color: COLORS.info,
    bgColor: COLORS.infoBg,
    format: 'currency',
    subtitle: 'Per sale',
  },
  {
    id: 'guestCount',
    title: 'Guest Count',
    icon: 'solar:users-group-rounded-bold',
    color: COLORS.warning,
    bgColor: COLORS.warningBg,
    format: 'number',
    subtitle: 'Customers',
  },
  {
    id: 'recentTransactions',
    title: 'Recent Transactions',
    icon: 'solar:chart-bold',
    color: COLORS.purple,
    bgColor: COLORS.purpleBg,
    format: 'number',
  },
  {
    id: 'completed',
    title: 'Completed',
    icon: 'solar:check-circle-bold',
    color: COLORS.pink,
    bgColor: COLORS.pinkBg,
    format: 'number',
  },
] as const;

/* =================== TABLE DEFAULTS =================== */

export const DEFAULT_ROWS_PER_PAGE = 10;
export const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

/* =================== VALIDATION LIMITS =================== */

export const INPUT_LIMITS = {
  // TEXT INPUT LIMITS
  TEXT_SHORT: 500, // Names, titles, short text
  TEXT_LONG: 5000, // Descriptions, notes, comments
  SEARCH_QUERY: 200, // Search and filter inputs
  EMAIL: 254, // RFC 5321 max email length
  PHONE: 20, // Phone number max length
  NUMERIC: 50, // Numeric string max length

  // PHONE VALIDATION
  PHONE_MIN_DIGITS: 3, // Minimum digits for valid phone

  // PAGINATION
  DAILY_BREAKDOWN_DESKTOP: 20,
  DAILY_BREAKDOWN_MOBILE: 10,
  TRANSACTIONS_DEFAULT: 7,
  STORE_DEFAULT_PAGE_SIZE: 50,
} as const;

/* =================== ACTIVITY ICONS =================== */

export const ACTIVITY_ICONS = {
  transaction: 'solar:bill-check-bold',
  customer: 'solar:user-check-bold',
  payout: 'solar:wallet-money-bold',
  refund: 'solar:undo-left-bold',
} as const;

/* =================== PERMISSIONS =================== */

export const AVAILABLE_PERMISSIONS: PermissionOption[] = [
  { value: 'manage_customers', label: 'Manage Customers' },
  { value: 'manage_transactions', label: 'Manage Transactions' },
  { value: 'view_reports', label: 'View Reports' },
  { value: 'manage_settings', label: 'Manage Settings' },
  { value: 'manage_employees', label: 'Manage Employees' },
];

/* =================== ROLE PERMISSION MAPPING =================== */

export const ROLE_PERMISSIONS: Record<EmployeeRole, string[]> = {
  [UserAccountType.BUSINESS_VIEWER]: ['view_reports'],
  [UserAccountType.BUSINESS_MANAGER]: [
    'manage_customers',
    'manage_transactions',
    'manage_employees',
  ],
  [UserAccountType.BUSINESS_ADMIN]: [
    'manage_customers',
    'manage_transactions',
    'view_reports',
    'manage_settings',
    'manage_employees',
  ],
  [UserAccountType.BUSINESS_CUSTOMER_USER]: [],
};
